import { useRef } from 'react';
import { useMachine } from '@xstate/react';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { useNotify } from '@hotjar/hotsauce';
import { EditOrderContainer } from './components/edit-order/EditOrderContainer';
import { ProcessOrderContainer } from './components/process-order/ProcessOrderContainer';
import { ConfirmOrderContainer } from './components/confirm-order/ConfirmOrderContainer';
import { checkoutMachine } from './state-machine/checkout.machine';
import { CheckoutLoader } from './components/initialize/CheckoutLoader';
import {
  useShortLinkWidgetHiddenState,
  usePageTitle,
} from '~/subscription-management/external-dependencies/webapp-utils';

export function Checkout(): JSX.Element {
  const [state, , service] = useMachine(checkoutMachine, {
    devTools: process.env.NODE_ENV === 'development',
    /**
     * some changes to the state machine in this MR (https://gitlab.com/hotjar/shared/insights-webapp/-/merge_requests/5875) caused the options object to throw type errors
     * Conrad Holtzhausen casted this to any for now so that he can go on his Dec 2022 holiday instead of going down the xstate typescript rabbit hole
     * TODO: (future Conrad) fix this the right way dammit
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } as any);
  const { t } = useTranslationWithPrefix('checkout:page-title');
  const { showToast } = useNotify();
  const hasToastShown = useRef(false);

  usePageTitle(t('checkout'));

  /**
   * Adding a debounce here as the onEvent is getting many NOTIFICATION events at a time.
   */
  setInterval(() => {
    hasToastShown.current = false;
  }, 5000);

  service.onEvent((event) => {
    if (event.type === 'NOTIFICATION' && !hasToastShown.current) {
      showToast({ kind: 'danger', text: 'Something went wrong. Please try again later' });
      hasToastShown.current = true;
    }
  });

  /**
   * WARNING: I am not proud of adding this here... This is just an interim step to move forward
   * TODO: we should fix the way the ShortLinkWidget show and hide, because the way it does today is inconsistent and causes it to show when you navigate from
   * other pages that has it on visible true, eventhough it should be hidden by the NoAppInterfaceRoute guard on this page
   */
  const { setIsHidden } = useShortLinkWidgetHiddenState();
  setIsHidden(true);

  // If we need to block the checkout because of any incident, uncomment this and change the wording inside ./IncidentAlert
  // return <IncidentAlert />;

  return (
    <>
      {state.matches('initialise') && <CheckoutLoader />}
      {state.matches('editOrder') && <EditOrderContainer checkoutService={service} />}
      {state.matches('confirmOrder') && <ConfirmOrderContainer checkoutService={service} />}
      {state.matches('processOrder') && <ProcessOrderContainer checkoutService={service} />}
    </>
  );
}
